import React from 'react'
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import type { ThongBaoMoi } from '@/apis/thongBaoApi'
import { ThongBaoLoaiAvatar, thongBaoAccent, thoiGianTuongDoi } from './thongBaoUiUtils'

export type ThongBaoItemRowProps = {
  item: ThongBaoMoi
  loaiLabel: string
  locale?: string
  xemLabel: string
  xoaLabel: string
  /** Bấm dòng: đánh dấu đã đọc + mở chứng từ. */
  onClick: () => void
  /** Có trang chi tiết → hiện nút "Xem". */
  coChiTiet: boolean
  onDelete: (e: React.MouseEvent) => void
  deleteDisabled?: boolean
}

/** 1 dòng thông báo trong panel chuông (kiểu YTL / Facebook). */
const ThongBaoItemRow: React.FC<ThongBaoItemRowProps> = ({
  item,
  loaiLabel,
  locale = 'vi',
  xemLabel,
  xoaLabel,
  onClick,
  coChiTiet,
  onDelete,
  deleteDisabled,
}) => {
  const chuaDoc = !item.watched
  const accent = thongBaoAccent(item.loaiThongBao)

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      sx={{
        display: 'flex',
        gap: 1.25,
        alignItems: 'flex-start',
        px: 1.5,
        py: 1.1,
        cursor: 'pointer',
        bgcolor: chuaDoc ? 'rgba(24, 119, 242, 0.08)' : 'transparent',
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': { bgcolor: chuaDoc ? 'rgba(24, 119, 242, 0.12)' : 'action.hover' },
        '&:hover .tb-xoa': { opacity: 0.8 },
      }}
    >
      <Box sx={{ mt: 0.25 }}>
        <ThongBaoLoaiAvatar loai={item.loaiThongBao} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: chuaDoc ? 700 : 600, color: 'text.primary', lineHeight: 1.35 }}
            >
              {item.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 0.25,
                color: chuaDoc ? 'text.primary' : 'text.secondary',
                lineHeight: 1.4,
                fontSize: '0.8125rem',
                wordBreak: 'break-word',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
              title={item.content}
            >
              {item.content}
            </Typography>
          </Box>
          {chuaDoc ? (
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#1877F2', flexShrink: 0, mt: 0.75 }} />
          ) : null}
        </Box>

        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1, minHeight: 24 }}>
          <Typography
            variant="caption"
            noWrap
            sx={{ flex: 1, minWidth: 0, color: chuaDoc ? '#1877F2' : 'text.secondary', fontWeight: chuaDoc ? 600 : 400 }}
          >
            {[loaiLabel, thoiGianTuongDoi(item.ngayTao, locale)].filter(Boolean).join(' · ')}
          </Typography>
          {coChiTiet ? (
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                onClick()
              }}
              endIcon={<ChevronRightIcon sx={{ fontSize: 16 }} />}
              sx={{
                flexShrink: 0,
                textTransform: 'none',
                fontWeight: 600,
                color: accent,
                minWidth: 0,
                px: 0.5,
                py: 0.1,
                '& .MuiButton-endIcon': { ml: 0.25 },
              }}
            >
              {xemLabel}
            </Button>
          ) : null}
        </Box>
      </Box>

      <Tooltip title={xoaLabel}>
        <span>
          <IconButton
            className="tb-xoa"
            size="small"
            aria-label={xoaLabel}
            onClick={onDelete}
            disabled={deleteDisabled}
            sx={{ mt: 0.25, opacity: 0.35, '&:hover': { opacity: 1, color: 'error.main' } }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  )
}

export default ThongBaoItemRow
